import { eur, pct } from "./format";

export const DEFAULT_INPUTS = {
  precio: 180000,
  alquiler: 950,
  itpPct: 10,
  notariaGastos: 2000,
  reforma: 15000,
  financiacionPct: 80,
  interes: 3.25,
  plazo: 30,
  gastosAnuales: 1200,
  gestionAlquilerPct: 0,
};

export const FIELDS = [
  { key: "precio", label: "Precio de compra", min: 40000, max: 800000, step: 5000, format: (v) => eur(v), testid: "input-precio-compra" },
  { key: "itpPct", label: "ITP / impuestos de compra", min: 0, max: 15, step: 0.5, format: (v) => pct(v, 1), testid: "input-itp" },
  { key: "notariaGastos", label: "Notaría, registro y gestoría", min: 0, max: 6000, step: 100, format: (v) => eur(v), testid: "input-notaria" },
  { key: "reforma", label: "Reforma y equipamiento", min: 0, max: 120000, step: 1000, format: (v) => eur(v), testid: "input-reforma" },
  { key: "alquiler", label: "Alquiler mensual estimado", min: 300, max: 4000, step: 25, format: (v) => eur(v), testid: "input-alquiler-mensual" },
  { key: "gestionAlquilerPct", label: "Gestión del alquiler (si la externalizas)", min: 0, max: 15, step: 0.5, format: (v) => pct(v, 1), testid: "input-gestion-alquiler" },
  { key: "gastosAnuales", label: "Gastos anuales (IBI, comunidad, seguro)", min: 0, max: 6000, step: 50, format: (v) => eur(v), testid: "input-gastos-anuales" },
  { key: "financiacionPct", label: "Financiación hipotecaria", min: 0, max: 100, step: 5, format: (v) => pct(v, 0), testid: "input-hipoteca-porcentaje" },
  { key: "interes", label: "Tipo de interés anual", min: 0, max: 8, step: 0.05, format: (v) => pct(v, 2), testid: "input-interes" },
  { key: "plazo", label: "Plazo de la hipoteca", min: 5, max: 40, step: 1, format: (v) => `${v} años`, testid: "input-plazo" },
];

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

export function calcularCuota(principal, interesAnualPct, anios) {
  const n = anios * 12;
  if (n <= 0 || principal <= 0) return 0;
  const i = interesAnualPct / 100 / 12;
  if (i === 0) return principal / n;
  const f = Math.pow(1 + i, n);
  return (principal * i * f) / (f - 1);
}

export function computeScore({ neta, cfMensual, cashOnCash, bruta }) {
  const sNeta = clamp(neta / 8, 0, 1) * 40;
  const sCf = clamp((cfMensual + 100) / 600, 0, 1) * 25;
  const sCoc = clamp(cashOnCash / 12, 0, 1) * 25;
  const sBruta = clamp(bruta / 10, 0, 1) * 10;
  return Math.round(clamp(sNeta + sCf + sCoc + sBruta, 0, 100));
}

export function scoreVerdict(score) {
  if (score >= 80) return { label: "Inversión excelente", tone: "emerald" };
  if (score >= 60) return { label: "Buena inversión", tone: "green" };
  if (score >= 40) return { label: "Analiza con cautela", tone: "amber" };
  return { label: "Riesgo elevado", tone: "red" };
}

export function analyze(inp) {
  const itpEuro = (inp.precio * inp.itpPct) / 100;
  const costeTotal = inp.precio + itpEuro + inp.notariaGastos + inp.reforma;
  const prestamo = (inp.precio * inp.financiacionPct) / 100;
  const cuota = calcularCuota(prestamo, inp.interes, inp.plazo);
  const entrada = Math.max(inp.precio - prestamo, 0);
  const inversionInicial = Math.max(costeTotal - prestamo, 0);
  const ingresosAnuales = inp.alquiler * 12;
  const gestionMensual = (inp.alquiler * inp.gestionAlquilerPct) / 100;
  const noi = ingresosAnuales - inp.gastosAnuales - gestionMensual * 12;
  const bruta = costeTotal > 0 ? (ingresosAnuales / costeTotal) * 100 : 0;
  const neta = costeTotal > 0 ? (noi / costeTotal) * 100 : 0;
  const cfMensual = inp.alquiler - gestionMensual - inp.gastosAnuales / 12 - cuota;
  const cfAnual = cfMensual * 12;
  const cashOnCash = inversionInicial > 0 ? (cfAnual / inversionInicial) * 100 : 0;
  const score = computeScore({ neta, cfMensual, cashOnCash, bruta });
  return {
    costeTotal, prestamo, cuota, entrada, inversionInicial, itpEuro, gestionMensual,
    ingresosAnuales, noi, bruta, neta, cfMensual, cfAnual, cashOnCash, score,
  };
}

// Tipos generales de ITP (vivienda usada) vigentes en 2026 por Comunidad Autónoma
export const NOTARIA_REGISTRO_PCT = 2;

export const CCAA_RATES = [
  { id: "andalucia", name: "Andalucía", itp: 7 },
  { id: "aragon", name: "Aragón", itp: 8 },
  { id: "asturias", name: "Asturias", itp: 8 },
  { id: "baleares", name: "Illes Balears", itp: 8, note: "escala del 8 % al 13 % según el valor" },
  { id: "canarias", name: "Canarias", itp: 6.5, note: "IGIC, tipo general" },
  { id: "cantabria", name: "Cantabria", itp: 10 },
  { id: "castilla-la-mancha", name: "Castilla-La Mancha", itp: 9 },
  { id: "castilla-y-leon", name: "Castilla y León", itp: 8 },
  { id: "cataluna", name: "Cataluña", itp: 10, note: "10 % hasta 600.000 €, tramos superiores por encima" },
  { id: "ceuta", name: "Ceuta", itp: 6 },
  { id: "comunidad-valenciana", name: "Comunitat Valenciana", itp: 9, note: "9 % desde junio de 2026; 11 % por encima de 1 M€" },
  { id: "extremadura", name: "Extremadura", itp: 8 },
  { id: "galicia", name: "Galicia", itp: 9 },
  { id: "la-rioja", name: "La Rioja", itp: 7 },
  { id: "madrid", name: "Comunidad de Madrid", itp: 6 },
  { id: "melilla", name: "Melilla", itp: 6 },
  { id: "murcia", name: "Región de Murcia", itp: 8 },
  { id: "navarra", name: "Navarra", itp: 6 },
  { id: "pais-vasco", name: "País Vasco", itp: 4 },
];
